import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { abortUpload, UploadProgress, UploadTask } from '@Utils/S3Storage'
import classnames from 'classnames'
import toastr from 'toastr'
import { v4 as uuidv4 } from 'uuid'
import some from 'lodash/some'
import ElementUploader, { IElementUploaderOptions, IUploadedElement } from '@Utils/ElementUploader'
import sanitizeUrlsString from '@Utils/SanitizeUrlsString'
import modifyUrls from '@Utils/ModifyUrls'
import validateUrls from '@Utils/ValidateUrls'

import Button from '@SiteComponents/Button'
import router from '@Site/Router'
import DragDropUploadZone from './DragDropUploadZone'
import SampleProjectButtons from './SampleProjectButtons'
import FeatureSelectionRow from './FeatureSelectionRow'
import LanguageSettingsRow from './LanguageSettingsRow'
import VoiceModeSelectionRow, { VoiceMode } from './VoiceModeSelectionRow'

import {
  ICreateElementPayload,
} from '@SiteModules/Element/types'

import {
  fetchQuickstartSettings,
  startQuickstartProcessing,
} from '@SiteModules/Onboarding/actions'

import {
  createElement,
} from '@SiteModules/Element/actions'

import {
  getAvailableProjectVersionLanguages,
  getAvailableTranslationLanguages,
  getDubbingLanguages,
  getHasLoadedProjectVersionSettingsSuccessfully,
  getVoiceCloningLanguages,
} from '@SiteModules/ProjectVersionSettings/selectors'

import {
  getCurrentWorkspaceId,
} from '@SiteModules/Workspace/selectors'

import {
  getCurrentAccountSubscriptionAllowsDubbing,
  getCurrentAccountSubscriptionAllowsVoiceCloning,
} from '@SiteModules/Account/selectors'

import './QuickStart.scss'

export type UploadState = 'idle' | 'uploading' | 'success'

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024 // 2 GB
const GOOGLE_DRIVE_URL_PATTERNS = ['docs.google.com', 'drive.google.com']
const PROJECT_VERSION_LANGUAGES_FOR_SAMPLE_PROJECTS = ['English']
const TRANSLATION_LANGUAGES_FOR_SAMPLE_PROJECTS = ['English', 'French', 'German', 'Spanish']

interface QuickstartProps {
  readonly hasJustStartedQuickstartProcessing: boolean
  setHasJustStartedQuickstartProcessing(newHasJustStartedQuickstartProcessing: boolean): void
}

const QuickStart: React.FC<QuickstartProps> = ({ hasJustStartedQuickstartProcessing, setHasJustStartedQuickstartProcessing }) => {
  const dispatch = useDispatch()

  const [selectedFeature, setSelectedFeature] = useState('viral-clips')
  const [voiceMode, setVoiceMode] = useState<VoiceMode>('ai-voices')
  const [originalLanguage, setOriginalLanguage] = useState('English')
  const [targetLanguage, setTargetLanguage] = useState('Spanish')
  const [selectedSample, setSelectedSample] = useState('')
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadedFileName, setUploadedFileName] = useState('')
  const [uploadedFileSize, setUploadedFileSize] = useState(0)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [createElementPayloadData, setCreateElementPayloadData] = useState<ICreateElementPayload | null>(null)
  const intervalRef = useRef<number | null>(null)
  const uploadRequestRef = useRef<UploadTask | null>(null)
  const elementIdRef = useRef<string | null>(null)

  const sampleSelected = selectedSample.length > 0
  const storeProjectVersionLanguages = useSelector(getAvailableProjectVersionLanguages)
  const storeTranslationLanguages = useSelector(getAvailableTranslationLanguages)
  const storeDubbingLanguages = useSelector(getDubbingLanguages)
  const storeVoiceCloningLanguages = useSelector(getVoiceCloningLanguages)

  const voiceCloningSelected = selectedFeature === 'ai-dubbing' && voiceMode === 'voice-cloning'
  const availableProjectVersionLanguages = sampleSelected
    ? PROJECT_VERSION_LANGUAGES_FOR_SAMPLE_PROJECTS
    : voiceCloningSelected ? storeVoiceCloningLanguages : storeProjectVersionLanguages
  const availableTranslationLanguages = sampleSelected
    ? TRANSLATION_LANGUAGES_FOR_SAMPLE_PROJECTS
    : voiceCloningSelected
      ? storeVoiceCloningLanguages
      : selectedFeature === 'ai-dubbing' ? storeDubbingLanguages : storeTranslationLanguages

  const hasLoadedSuccessfully = useSelector(getHasLoadedProjectVersionSettingsSuccessfully)
  const currentWorkspaceId = useSelector(getCurrentWorkspaceId)
  const aiDubbingAvailable = useSelector(getCurrentAccountSubscriptionAllowsDubbing)
  const voiceCloningAvailable = useSelector(getCurrentAccountSubscriptionAllowsVoiceCloning)
  const youtubeUrlGiven = youtubeUrl.length > 0
  const modifiedAndSanitizedUrl = modifyUrls(sanitizeUrlsString(youtubeUrl))[0]

  useEffect(() => {
    dispatch(fetchQuickstartSettings())
  }, [])

  useEffect(() => {
    if (!aiDubbingAvailable && selectedFeature === 'ai-dubbing') {
      setSelectedFeature('subtitles')
    }
  }, [aiDubbingAvailable, selectedFeature])

  const clearUploadInterval = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  useEffect(() => {
    return () => {
      clearUploadInterval()
    }
  }, [])

  useEffect(() => {
    return () => {
      if (uploadRequestRef.current) {
        void abortUpload(uploadRequestRef.current)
      }
    }
  }, [])

  useEffect(() => {
    const shouldResetUpload = elementIdRef.current !== null && (youtubeUrlGiven || sampleSelected)

    if (!shouldResetUpload) { return }

    void handleCancelUpload()

    if (sampleSelected) {
      setYoutubeUrl('')
    }
  }, [youtubeUrlGiven, sampleSelected])

  useEffect(() => {
    if (youtubeUrlGiven && sampleSelected) {
      setSelectedSample('')
    }
  }, [youtubeUrlGiven])

  useEffect(() => {
    if (sampleSelected && youtubeUrlGiven) {
      setYoutubeUrl('')
    }
  }, [sampleSelected])

  const handleFileSelected = async (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      toastr.error('Your file is too large (> 2MB).', 'Error')
      return
    }

    if (sampleSelected) {
      setSelectedSample('')
    }

    if (youtubeUrlGiven) {
      setYoutubeUrl('')
    }

    const elementId = uuidv4()
    elementIdRef.current = elementId

    setUploadedFileName(file.name)
    setUploadedFileSize(file.size)
    setUploadState('uploading')
    setUploadProgress(0)

    const options: IElementUploaderOptions = {
      workspaceId: currentWorkspaceId,
      uploadElementCallback: (params: IUploadedElement) => {
        setUploadProgress(100)
        setUploadState('success')

        setCreateElementPayloadData({
          ...params,
          workspaceId: currentWorkspaceId,
          elementId
        })
      },
      showProgressCallback: (data: UploadProgress) => {
        const progress = ((data.loaded ?? 0) / file.size) * 100
        setUploadProgress(Math.round(progress))
      }
    }

    const uploadRequest = new ElementUploader(file, options).call()
    uploadRequestRef.current = uploadRequest
  }

  const handleCancelUpload = async () => {
    if (uploadRequestRef.current) {
      const uploadAborted = await abortUpload(uploadRequestRef.current)
      if (!uploadAborted) { return }

      uploadRequestRef.current = null
    }

    setUploadState('idle')
    setUploadProgress(0)
    setUploadedFileName('')
    setUploadedFileSize(0)
    elementIdRef.current = null
    setCreateElementPayloadData(null)
  }

  const checkUrl = () => {
    if (!validateUrls([youtubeUrl]) || !modifiedAndSanitizedUrl) {
      toastr.error('The URL is invalid. Please check and try again.', 'Error')

      return false
    } else if (some(GOOGLE_DRIVE_URL_PATTERNS, pattern => modifiedAndSanitizedUrl.includes(pattern))) {
      toastr.error('Google Drive links are not supported. Please upload file directly.', 'Error')

      return false
    }

    return true
  }

  const handleStartGeneration = () => {
    if (youtubeUrlGiven && !checkUrl()) { return }
    if (selectedFeature === 'ai-dubbing' && !aiDubbingAvailable) { return }

    const repurposing = selectedFeature === 'viral-clips'

    const startQuickstartProcessingPayloadData = {
      workspaceId: currentWorkspaceId,
      projectVersionLanguage: originalLanguage,
      translationLanguage: targetLanguage,
      dubbing: selectedFeature === 'ai-dubbing',
      voiceCloning: selectedFeature === 'ai-dubbing' && voiceMode === 'voice-cloning',
      repurposing,
      ...(sampleSelected ? { selectedSample } : {}),
      ...(youtubeUrlGiven ? { videoUrl: youtubeUrl } : {})
    }

    if (createElementPayloadData) {
      dispatch(createElement({ ...createElementPayloadData, quickstart: true, repurposing, startQuickstartProcessingPayloadData }))
    } else {
      dispatch(startQuickstartProcessing(startQuickstartProcessingPayloadData))
    }

    setHasJustStartedQuickstartProcessing(true)
  }

  const handleVoiceCloningUpgrade = () => {
    void router.navigate('/subscriptions')
  }

  const setUpCompleted = (originalLanguage.length > 0 && targetLanguage.length > 0) && (sampleSelected || youtubeUrlGiven || uploadState === 'success')

  const isCtaDisabled = !hasLoadedSuccessfully ||
    currentWorkspaceId < 0 ||
    hasJustStartedQuickstartProcessing ||
    !setUpCompleted

  return (
    <div className="QuickStart">
      <div className="QuickStart__container">
        <div className="QuickStart__header">
          <h1 className="QuickStart__title">Let&apos;s create your first project.</h1>
          <p className="QuickStart__subtitle">Upload a video, pick your features, and let AI do the rest.</p>
        </div>

        <DragDropUploadZone
          uploadState={uploadState}
          uploadProgress={uploadProgress}
          fileName={uploadedFileName}
          fileSize={uploadedFileSize}
          onFileSelected={handleFileSelected}
          onCancelUpload={handleCancelUpload}
          youtubeUrl={youtubeUrl}
          setYoutubeUrl={setYoutubeUrl}
        />

        {false && (
          <SampleProjectButtons
            selectedSample={selectedSample}
            onSelectSample={setSelectedSample}
          />
        )}

        <FeatureSelectionRow
          selectedId={selectedFeature}
          aiDubbingAvailable={aiDubbingAvailable}
          onSelect={setSelectedFeature}
        />

        {selectedFeature === 'ai-dubbing' && (
          <VoiceModeSelectionRow
            selectedMode={voiceMode}
            voiceCloningAvailable={voiceCloningAvailable}
            onSelect={setVoiceMode}
            onUpgrade={handleVoiceCloningUpgrade}
          />
        )}

        <LanguageSettingsRow
          availableOriginalLanguages={availableProjectVersionLanguages}
          availableTargetLanguages={availableTranslationLanguages}
          hasLoadedSuccessfully={hasLoadedSuccessfully}
          originalLanguage={originalLanguage}
          targetLanguage={targetLanguage}
          onOriginalChange={setOriginalLanguage}
          onTargetChange={setTargetLanguage}
          setOriginalLanguage={setOriginalLanguage}
          setTargetLanguage={setTargetLanguage}
        />

        <div
          className={classnames(
            'QuickStart__cta-wrapper',
            { 'QuickStart__cta-wrapper-disabled': isCtaDisabled}
          )}
        >
          <Button
            styleType="brand"
            size="large"
            caption="Start Magic Generation ✨"
            data-testid="quickstart-submit"
            onClick={handleStartGeneration}
            disabled={isCtaDisabled}
          />
        </div>

        <p className="QuickStart__footer-text">
          Supports MP4, MOV, AVI, MKV up to 2GB · YouTube, Vimeo URLs supported
        </p>
      </div>
    </div>
  )
}

export default QuickStart
